/**
 * iter287 — Aide en ligne de l'atelier (comptes `atelier`).
 * Bulle en bas à droite : on demande « comment je fais pour… » et l'aide
 * explique la manipulation. Elle ne voit aucune donnée du CRM et ne fait
 * aucune action : c'est un mode d'emploi, pas un assistant opérationnel.
 */
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LifeBuoy, X, Send, Loader2, Plus } from "lucide-react";
import api from "@/lib/api";

const SUGGESTIONS = [
  "Comment je demande des congés ?",
  "Où je vois seulement mes OR ?",
  "Comment j'ajoute de la main-d'œuvre sur un OR ?",
  "Comment je crée un OR depuis une carte grise ?",
  "J'ai oublié de badger ce matin, je fais quoi ?",
  "Que veut dire « à monter » dans le gardiennage pneus ?",
];

export const AideFab = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [texte, setTexte] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const fin = useRef(null);

  useEffect(() => { fin.current?.scrollIntoView({ block: "nearest" }); }, [messages, busy]);

  const envoyer = async (question) => {
    const q = (question ?? texte).trim();
    if (!q || busy) return;
    setTexte("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/aide/chat", { message: q, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: e.response?.data?.detail || "L'aide est momentanément indisponible.",
        erreur: true,
      }]);
    } finally {
      setBusy(false);
    }
  };

  const nouvelle = () => { setMessages([]); setSessionId(null); setTexte(""); };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-[#52a675] text-white shadow-lg shadow-[#52a675]/40 flex items-center justify-center hover:bg-[#3d8a5c] hover:scale-105 transition-all"
          title="Aide : comment utiliser le CRM"
          data-testid="aide-fab"
        >
          <LifeBuoy className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-4 right-4 z-50 w-[calc(100vw-2rem)] sm:w-[400px] h-[min(600px,calc(100vh-2rem))] bg-white border border-gray-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="aide-panel"
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-[#f0f9f4]">
            <LifeBuoy className="w-4 h-4 text-[#52a675]" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900">Aide CRM</div>
              <div className="text-[10px] text-gray-500">
                Comment ça marche, où trouver quoi — aucune donnée consultée
              </div>
            </div>
            <button onClick={nouvelle} className="text-gray-400 hover:text-gray-700 p-1" title="Nouvelle question" data-testid="aide-new">
              <Plus className="w-4 h-4" />
            </button>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700 p-1" title="Fermer" data-testid="aide-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {!messages.length && (
              <div className="space-y-2" data-testid="aide-suggestions">
                <p className="text-xs text-gray-500">
                  Une question sur le CRM ? Demande, ou commence par :
                </p>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => envoyer(s)}
                    className="w-full text-left text-xs px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-[#52a675] hover:bg-[#f0f9f4] transition-colors"
                    data-testid="aide-suggestion"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
                data-testid={`aide-message-${m.role}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#52a675] text-white rounded-br-sm"
                      : m.erreur
                        ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-sm"
                        : "bg-gray-100 text-gray-800 rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex items-center gap-2 text-xs text-gray-400" data-testid="aide-loading">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> L&apos;aide cherche la bonne marche à suivre…
              </div>
            )}
            <div ref={fin} />
          </div>

          <div className="border-t border-gray-100 p-2 flex items-end gap-2">
            <Textarea
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); envoyer(); }
              }}
              placeholder="Comment je fais pour… ?"
              rows={1}
              className="resize-none text-sm min-h-[40px] max-h-28"
              data-testid="aide-input"
            />
            <Button
              onClick={() => envoyer()}
              disabled={busy || !texte.trim()}
              className="h-10 w-10 p-0 bg-[#52a675] hover:bg-[#3d8a5c] flex-shrink-0"
              data-testid="aide-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default AideFab;
